import { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, Clock, FileText, CalendarDays,
  Upload, Folder, Users, History, Download, LogOut, Menu, X, Plane,
  CalendarRange, User,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { flagFor } from "@/lib/countries";
import { API } from "@/lib/api";

const LOGO = "https://customer-assets.emergentagent.com/job_2a175c3d-670a-4806-867b-48dd0b5b1184/artifacts/d7ah6vlh_cropped-3-1.png";

const NAV_ALL = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, all: true, end: true },
  { to: "/calendar", label: "Calendar", icon: CalendarRange, all: true },
  { to: "/projects", label: "Projects", icon: FolderKanban, all: true },
  { to: "/time", label: "Time Tracking", icon: Clock, all: true },
  { to: "/daily-reports", label: "Daily Reports", icon: FileText, all: true },
  { to: "/leaves", label: "Leave", icon: CalendarDays, all: true },
  { to: "/uploads", label: "Uploads", icon: Upload, all: true },
  { to: "/documents", label: "Documents", icon: Folder, all: true },
  { to: "/users", label: "Employees", icon: Users, adminOnly: true },
  { to: "/exports", label: "Exports", icon: Download, adminOnly: true },
  { to: "/audit", label: "Audit Log", icon: History, adminOnly: true },
  { to: "/profile", label: "My Profile", icon: User, all: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const items = NAV_ALL.filter((i) => i.all || (i.adminOnly && user.role === "admin"));

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex bg-black text-white">
      {/* Sidebar — desktop */}
      <aside className="hidden lg:flex w-64 shrink-0 border-r border-[#1c1c1c] flex-col bg-brand-vertical">
        <div className="p-5 border-b border-[#1c1c1c] flex items-center gap-3 bg-brand-soft">
          <img src={LOGO} alt="" className="h-16 w-16" />
          <div className="leading-tight">
            <div className="font-heading font-bold uppercase tracking-tight text-sm">Chongie Aviation</div>
            <div className="label-eyebrow" style={{ color: "#8a83d4" }}>Workforce</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`nav-${it.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 text-sm font-medium border-l-2 transition-colors",
                  isActive
                    ? "bg-[#100755]/40 border-[#3b2bd0] text-white"
                    : "border-transparent text-[#888] hover:text-white hover:bg-[#100755]/15"
                )
              }
            >
              <it.icon className="h-4 w-4" /> {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-[#1c1c1c]">
          <div className="flex items-center gap-3 mb-3">
            <div className="h-9 w-9 grid place-items-center overflow-hidden border border-[#3b2bd0]" style={{ backgroundColor: "#100755" }}>
              {user?.avatar_filename ? (
                <img src={`${API}/users/${user.id}/avatar`} alt="" className="h-full w-full object-cover" />
              ) : (
                <span className="font-heading font-bold uppercase text-white">{user?.name?.[0] || "U"}</span>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate flex items-center gap-1">
                {user?.name}
                {user?.country && <span className="text-xs" title={user.country}>{flagFor(user.country)}</span>}
              </div>
              <div className="label-eyebrow truncate">{user?.role}</div>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full justify-start text-[#888] hover:text-white hover:bg-[#0a0a0a] rounded-none border border-[#1c1c1c]"
          >
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <header
        className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-brand-vertical border-b border-[#1c1c1c] safe-top"
        data-testid="mobile-header"
      >
        <div className="flex items-center justify-between px-3 h-14 max-w-full">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <img src={LOGO} alt="Chongie Aviation" className="h-9 w-9 shrink-0" />
            <div className="min-w-0 leading-tight">
              <div className="font-heading font-bold uppercase tracking-tight text-[13px] truncate">Chongie Aviation</div>
              <div className="label-eyebrow text-[9px]" style={{ color: "#8a83d4" }}>Workforce</div>
            </div>
          </div>
          <button
            onClick={() => setOpen(!open)}
            className="shrink-0 ml-2 p-2 border border-[#3b2bd0]/40 bg-[#100755]/40 active:bg-[#100755]/70 transition-colors"
            data-testid="mobile-menu-toggle"
            aria-label="menu"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      {open && (
        <>
          <div
            className="lg:hidden fixed inset-0 z-30 bg-black/60 backdrop-blur-sm"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className="lg:hidden fixed top-14 left-0 right-0 bottom-0 z-40 bg-brand-vertical overflow-y-auto safe-bottom" data-testid="mobile-nav">
            <nav className="p-3 space-y-1">
              {items.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  end={it.end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-3 px-4 py-3 text-base font-medium border border-[#1c1c1c]",
                      isActive ? "border-[#3b2bd0] bg-[#100755] text-white" : "bg-[#0a0a0a] text-white"
                    )
                  }
                >
                  <it.icon className="h-5 w-5 shrink-0" /> {it.label}
                </NavLink>
              ))}
              <div className="pt-3 mt-3 border-t border-[#1c1c1c] flex items-center gap-3 px-2">
                <div className="h-9 w-9 grid place-items-center overflow-hidden border border-[#3b2bd0] shrink-0" style={{ backgroundColor: "#100755" }}>
                  {user?.avatar_filename ? (
                    <img src={`${API}/users/${user.id}/avatar`} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <span className="font-heading font-bold uppercase text-white">{user?.name?.[0] || "U"}</span>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate flex items-center gap-1">
                    {user?.name}
                    {user?.country && <span className="text-xs shrink-0" title={user.country}>{flagFor(user.country)}</span>}
                  </div>
                  <div className="label-eyebrow truncate">{user?.role}</div>
                </div>
              </div>
              <button
                onClick={handleLogout}
                data-testid="mobile-logout-btn"
                className="w-full text-left flex items-center gap-3 px-4 py-3 mt-2 border border-[#1c1c1c] text-[#888]"
              >
                <LogOut className="h-5 w-5" /> Sign out
              </button>
            </nav>
          </div>
        </>
      )}

      {/* Main */}
      <main className="flex-1 min-w-0 max-w-full lg:pl-0 pt-14 lg:pt-0 pb-8 lg:pb-0">
        <div className="max-w-[1500px] mx-auto p-3 sm:p-4 lg:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
